import { createSignal, createContext, useContext, type ParentComponent, type Accessor } from "solid-js";

interface UIState {
  sidebarOpen: Accessor<boolean>;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  commandPaletteOpen: Accessor<boolean>;
  setCommandPaletteOpen: (open: boolean) => void;
  openCommandPalette: (prefill?: string) => void;
  commandPalettePrefill: Accessor<string>;
  clearCommandPalettePrefill: () => void;
  mobileMenuOpen: Accessor<boolean>;
  setMobileMenuOpen: (open: boolean) => void;
  theme: Accessor<"light" | "dark" | "system">;
  setTheme: (theme: "light" | "dark" | "system") => void;
}

const UIContext = createContext<UIState>();

export const UIProvider: ParentComponent = (props) => {
  const [sidebarOpen, setSidebarOpen] = createSignal(true);
  const [commandPaletteOpen, setCommandPaletteOpen] = createSignal(false);
  const [commandPalettePrefill, setCommandPalettePrefill] = createSignal("");
  const [mobileMenuOpen, setMobileMenuOpen] = createSignal(false);
  const [theme, setTheme] = createSignal<"light" | "dark" | "system">("system");

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen());
  const openCommandPalette = (prefill = "") => {
    setCommandPalettePrefill(prefill);
    setCommandPaletteOpen(true);
  };
  const clearCommandPalettePrefill = () => setCommandPalettePrefill("");

  const store: UIState = {
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar,
    commandPaletteOpen,
    setCommandPaletteOpen,
    openCommandPalette,
    commandPalettePrefill,
    clearCommandPalettePrefill,
    mobileMenuOpen,
    setMobileMenuOpen,
    theme,
    setTheme,
  };

  return (
    <UIContext.Provider value={store}>
      {props.children}
    </UIContext.Provider>
  );
};

export function useUI() {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error("useUI must be used within a UIProvider");
  }
  return context;
}

interface VideoWorkbenchState {
  currentVideoId: Accessor<string | null>;
  setCurrentVideoId: (id: string | null) => void;
  currentTime: Accessor<number>;
  setCurrentTime: (time: number) => void;
  isPlaying: Accessor<boolean>;
  setIsPlaying: (playing: boolean) => void;
  activeTab: Accessor<"overview" | "transcript" | "visuals">;
  setActiveTab: (tab: "overview" | "transcript" | "visuals") => void;
}

const VideoWorkbenchContext = createContext<VideoWorkbenchState>();

export const VideoWorkbenchProvider: ParentComponent = (props) => {
  const [currentVideoId, setCurrentVideoId] = createSignal<string | null>(null);
  const [currentTime, setCurrentTime] = createSignal(0);
  const [isPlaying, setIsPlaying] = createSignal(false);
  const [activeTab, setActiveTab] = createSignal<"overview" | "transcript" | "visuals">("overview");

  const store: VideoWorkbenchState = {
    currentVideoId,
    setCurrentVideoId,
    currentTime,
    setCurrentTime,
    isPlaying,
    setIsPlaying,
    activeTab,
    setActiveTab,
  };

  return (
    <VideoWorkbenchContext.Provider value={store}>
      {props.children}
    </VideoWorkbenchContext.Provider>
  );
};

export function useVideoWorkbench() {
  const context = useContext(VideoWorkbenchContext);
  if (!context) {
    throw new Error("useVideoWorkbench must be used within a VideoWorkbenchProvider");
  }
  return context;
}

interface SelectionState {
  selectedVideoIds: Accessor<Set<string>>;
  toggleVideoSelection: (id: string) => void;
  selectAllVideos: (ids: string[]) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;
}

const SelectionContext = createContext<SelectionState>();

export const SelectionProvider: ParentComponent = (props) => {
  const [selectedVideoIds, setSelectedVideoIds] = createSignal<Set<string>>(new Set());

  const toggleVideoSelection = (id: string) => {
    setSelectedVideoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllVideos = (ids: string[]) => {
    setSelectedVideoIds(new Set<string>(ids));
  };

  const clearSelection = () => {
    setSelectedVideoIds(new Set<string>());
  };

  const isSelected = (id: string) => selectedVideoIds().has(id);

  const store: SelectionState = {
    selectedVideoIds,
    toggleVideoSelection,
    selectAllVideos,
    clearSelection,
    isSelected,
  };

  return (
    <SelectionContext.Provider value={store}>
      {props.children}
    </SelectionContext.Provider>
  );
};

export function useSelection() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error("useSelection must be used within a SelectionProvider");
  }
  return context;
}
